// Cash Flow Report Page
import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Wallet, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { useCashFlowReport } from '@/features/reports/hooks/useCashFlowReport';
import { PeriodSelector } from '@/features/reports/components/PeriodSelector';
import { MetricCard } from '@/features/reports/components/MetricCard';
import { ExportButton } from '@/features/reports/components/ExportButton';
import { CashFlowTrendChart, IncomeVsExpenseChart } from '@/features/reports/components/CashFlowChart';
import { getThisMonth } from '@/features/reports/utils/periods';
import { formatCurrency } from '@/features/reports/utils/calculations';
import type { ExportFormat } from '@/types/report';

export const Route = createFileRoute('/apps/reports/cashflow')({  
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: CashFlowReportPage,
});

function CashFlowReportPage() {
  const [period, setPeriod] = useState(getThisMonth());
  const { data, isLoading, error } = useCashFlowReport(period);

  const handleExport = async (format: ExportFormat) => {
    console.log('Exporting cash flow report as', format);
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur lors du chargement du rapport: {error.message}
          </p>
        </div>
      </div>
    );
  }

  const netCashFlow = data?.metrics.netCashFlow || 0;
  const isPositive = netCashFlow >= 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <span className="font-arabic">تقرير التدفق النقدي</span>
            <span className="text-muted-foreground mx-2">/</span>
            <span>Rapport de trésorerie</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Analyse de vos flux de trésorerie et position financière
          </p>
        </div>
        
        <ExportButton onExport={handleExport} disabled={isLoading || !data} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Period Selector */}
        <div className="lg:col-span-1">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {/* Metrics */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="Recettes"
            titleAr="الإيرادات"
            value={data?.metrics.totalIncome || 0}
            format="currency"
            icon={DollarSign}
            color="green"
          />
          
          <MetricCard
            title="Dépenses"
            titleAr="المصاريف"
            value={data?.metrics.totalExpenses || 0}
            format="currency"
            icon={Wallet}
            color="red"
          />
          
          <MetricCard
            title="Flux net"
            titleAr="التدفق الصافي"
            value={netCashFlow}
            format="currency"
            icon={TrendingUp}
            color={isPositive ? 'green' : 'red'}
            subtitle={isPositive ? 'Positif' : 'Négatif'}
          />
          
          <MetricCard
            title="Solde final"
            titleAr="الرصيد النهائي"
            value={data?.metrics.closingBalance || 0}
            format="currency"
            icon={Wallet}
            color="blue"
            subtitle={`Début: ${formatCurrency(data?.metrics.openingBalance || 0)}`}
          />
        </div>
      </div>

      {/* Charts */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-80 bg-card border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      ) : data ? (
        <div className="space-y-6">
          <CashFlowTrendChart data={data.trend} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <IncomeVsExpenseChart data={data.trend} />
            
            {/* Payment Methods Breakdown */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <span className="font-arabic">حسب وسيلة الدفع</span>
                <span className="text-muted-foreground mx-2">/</span>
                <span>Par méthode de paiement</span>
              </h3>
              
              <div className="space-y-3">
                {data.byMethod.map((method, index) => {
                  const isNet = method.net >= 0;
                  return (
                    <div
                      key={index}
                      className="p-4 bg-background rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-foreground">
                          {method.method}
                        </p>
                        <p className={`text-sm font-semibold ${isNet ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(method.net)}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Recettes:</span>
                          <span className="text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(method.income)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Dépenses:</span>
                          <span className="text-red-600 dark:text-red-400">
                            {formatCurrency(method.expense)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Forecast */}
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-foreground">
                <span className="font-arabic">التوقعات</span>
                <span className="text-muted-foreground mx-2">/</span>
                <span>Prévisions de trésorerie</span>
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">7 jours</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(data.forecast.days7)}
                </p>
              </div>
              <div className="p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">30 jours</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(data.forecast.days30)}
                </p>
              </div>
              <div className="p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">90 jours</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(data.forecast.days90)}
                </p>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground mt-4">
              * Prévisions basées sur la tendance historique. Ces estimations sont indicatives.
            </p>
          </div>
        </div>
      ) : null}
      </div>
    </DashboardLayout>
  );
}
